import { call, put, select } from 'redux-saga/effects';
import applicationSpinnerActions from 'redux/actions/applicationSpinnerActions';
import {
  calculateDistanceEngineSelector,
  garageOrientationOptionsSelector,
  partnerSelector,
  selectMoveVersion,
  selectTenantId,
  showConfirmationFollowUpPageSelector,
} from 'redux/selectors/config';
import { getSynergySessionToken } from 'utils/sagas-utils';
import {
  caseDataCreatedSelector,
  caseGuidCreatedSelector,
  locationCaseCreatedSelector,
} from 'redux/selectors/caseCreation';
import { sendSelectedDestinationApi } from 'api/destination/sendSelectedDestination';
import { convertMilesToKm } from 'utils/global-utils';
import {
  categorySelector,
  destinationChoicesSelector,
  destinationCoordinatesSelector,
  serviceIdSelector,
} from 'redux/selectors/destination';
import {
  parseCoordinatesToBing,
  isBingMapCoordinate,
} from 'containers/Location/components/LocationWithBing/useLocationWithBing';
import { getDistance } from 'api/getDistance';
import type {
  GoogleMapCoordinates,
  ChoiceResponse,
  GetDistanceResponse,
  BingMapCoordinates,
  LocationDetails,
} from 'ts/types';
import { ApiResponse } from 'ts/interfaces/api-response-getDistance.interface';
import destinationActions from 'redux/actions/destinationActions';
import { routes } from 'Routes/routesConfig';
import { push } from 'connected-react-router';
import type { sendSelectedDestinationActionType } from 'redux/actions/destinationAction.types';
import getGarageOrientationSearchIdApi from 'api/destination/garageOrientationSearchId';
import getGarageOrientationApi from 'api/destination/getGarageOrientation';
import { getDestinationData, showMaxDistanceAlert } from './utils';
import type { DestinationData } from 'ts/types/destination.types';

const { sendSelectedDestination } = destinationActions;

function* handleResponse(status: number) {
  const shouldRedirectToConfirmationFollowUp: boolean = yield select(
    showConfirmationFollowUpPageSelector,
  );

  if (status === 204) {
    yield put(
      shouldRedirectToConfirmationFollowUp
        ? push(routes.SYNERGY_FOLLOW_CONFIRMATION)
        : push(routes.CONFIRMATION),
    );
  } else if (status === 202) {
    yield put(push(routes.CALLBACK));
  } else {
    yield put(push(routes.CONTACT_US));
  }

  return false;
}

export function* sendSelectedDestinationSaga(
  action: sendSelectedDestinationActionType,
) {
  const { startLoad, stopLoad } = applicationSpinnerActions;
  const choiceId = action.payload;
  try {
    yield put(startLoad({}));

    const tenantId: string = yield select(selectTenantId);
    const moveVersion: string = yield select(selectMoveVersion);
    const sessionToken: string = yield getSynergySessionToken();
    const serviceId: string = yield select(serviceIdSelector);
    const caseGuid: ReturnType<typeof caseGuidCreatedSelector> = yield select(
      caseGuidCreatedSelector,
    );
    const data: DestinationData = yield getDestinationData(choiceId);

    const response: ApiResponse<any> = yield call(sendSelectedDestinationApi, {
      body: { destination: data },
      caseGuid,
      serviceId,
      token: sessionToken,
      tenantId,
      moveVersion,
    });
    const { status } = response;
    yield handleResponse(status);
  } catch (error) {
    console.error('Error while sending selected destination (saga)', error);
    yield put(push(routes.DROPOFF));
  } finally {
    yield put(stopLoad({}));
  }
}

export function selectChoice(
  categorySelected: string,
  choices: ChoiceResponse[],
) {
  let choiceSelected;
  if (categorySelected && choices) {
    choiceSelected = choices.find(
      choice => choice.category === categorySelected,
    );
  }
  return choiceSelected;
}

export function* getDestinationDistanceSaga() {
  try {
    let destinationCoordFormatted: BingMapCoordinates = {},
      locationCoordFormatted: BingMapCoordinates = {};
    const categorySelected: string = yield select(categorySelector);
    const destinationCoords: BingMapCoordinates | GoogleMapCoordinates =
      yield select(destinationCoordinatesSelector);
    const currentLocationCoord: BingMapCoordinates = yield select(
      locationCaseCreatedSelector,
    );
    const choices: ChoiceResponse[] = yield select(destinationChoicesSelector);
    const choiceSelected: ChoiceResponse = yield selectChoice(
      categorySelected,
      choices,
    );
    const engine: string = yield select(calculateDistanceEngineSelector);

    if (choiceSelected) {
      const {
        maximum_distance: { unit = '', value: maxDistanceValue = 0 } = {},
      } = choiceSelected;

      const isMaximumDistanceUnitMiles = unit === 'Mile' || unit === '[MI_I]';

      const allowedDistance = isMaximumDistanceUnitMiles
        ? convertMilesToKm(maxDistanceValue as number)
        : maxDistanceValue;

      destinationCoordFormatted = yield parseCoordinatesToBing(
        destinationCoords,
      );
      locationCoordFormatted = yield parseCoordinatesToBing(
        currentLocationCoord,
      );
      if (isBingMapCoordinate(destinationCoordFormatted)) {
        const body = {
          originCoordinates: {
            ...locationCoordFormatted,
          },
          destinationCoordinates: {
            ...destinationCoordFormatted,
          },
          engine,
        };
        const response: ApiResponse<GetDistanceResponse> = yield call(
          getDistance,
          body,
        );

        if (response?.result?.travelDistance <= allowedDistance) {
          yield put(sendSelectedDestination(categorySelected));
        } else {
          yield showMaxDistanceAlert(
            isMaximumDistanceUnitMiles,
            maxDistanceValue,
            allowedDistance as number,
          );
        }

        return;
      }
      throw new Error('Bad formatting for coordinates');
    }
    throw new Error('No option selected for Destination');
  } catch (error) {
    console.error(error);
    yield put(push(routes.DROPOFF));
  }
}

export function* getGarageOrientationSaga() {
  try {
    yield put(applicationSpinnerActions.startLoad({}));

    const tenantId: string = yield select(selectTenantId);
    const moveVersion: string = yield select(selectMoveVersion);
    const token: string = yield getSynergySessionToken();
    const partner: string = yield select(partnerSelector);

    const caseData: ReturnType<typeof caseDataCreatedSelector> = yield select(
      caseDataCreatedSelector,
    ) as any;

    const options: ReturnType<typeof garageOrientationOptionsSelector> =
      yield select(garageOrientationOptionsSelector);

    const getSearchIdBody = {
      service: {
        service_code: 'GARAGE',
      },
      starting_location: (caseData?.current_location as LocationDetails)
        ?.coordinates,
      ...options,
    };

    // 1. Get searchId
    const searchParamsRes: ApiResponse<any> = yield call(
      // @ts-ignore
      getGarageOrientationSearchIdApi,
      {
        body: getSearchIdBody,
        moveVersion,
        partner,
        tenantId,
        token,
      },
    );

    if (!searchParamsRes?.result?.search_request_id) {
      throw new Error('No search_request_id was provided');
    }

    // 2. Get garages with searchId
    const res: ApiResponse<any> = yield call(getGarageOrientationApi, {
      searchId: searchParamsRes.result.search_request_id,
      moveVersion,
      partner,
      tenantId,
      token,
    });

    if (!res?.result?.providers) {
      throw new Error('No providers were found');
    }
    yield put(
      destinationActions.setGarageOrientationData(res.result.providers),
    );
    yield put(push(routes.GARAGE_ORIENTATION));
  } catch (error) {
    console.error({ error });
  } finally {
    yield put(applicationSpinnerActions.stopLoad({}));
  }
}
